import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import SectionHeader from '@/components/ui/SectionHeader';
import WhyBox from '@/components/ui/WhyBox';
import DownloadProjectButton from '@/components/production/outputs/DownloadProjectButton';
import PromoteStyleButton from '@/components/production/outputs/PromoteStyleButton';
import SendToCuttingRoomButton from '@/components/production/outputs/SendToCuttingRoomButton';
import type { ProductionState } from '@/hooks/useProduction';

/** Final asset filenames are keyed off the film's title. */
export function finalTitle(state: ProductionState): string {
  const breakdown = state.scriptBreakdown as { title?: string } | null;
  return (breakdown?.title || 'PRODUCTION').toUpperCase().replace(/\s+/g, '_');
}

/**
 * The finished film plus every way of getting it out of Story Engine — the video
 * itself, edit lists for a real NLE, and the full project archive.
 */
export default function FinalOutput({ state }: { state: ProductionState }) {
  const { t } = useTranslation('production');
  // Downloads stay available whenever the production exists: clips, the EDL and the
  // editorial project are all pullable even before the run is marked 'complete' (and
  // the status can lag a resume). Hide only when there is no production at all.
  if (!state.id) return null;

  const complete = state.status === 'complete';
  const title = finalTitle(state);

  return (
    <div>
      <SectionHeader title={complete ? t('final.ready') : t('final.outputs')} help={t('final.helpText')} />

      {!state.finalVideo && (
        <WhyBox variant="danger" title={t('final.noVideoTitle')} className="mb-4 max-w-2xl">
          {t('final.noVideo')}
        </WhyBox>
      )}

      {state.finalVideo && <video src={state.finalVideo} controls className="mb-4 w-full rounded-lg border border-border" />}

      <div className="flex flex-wrap gap-2">
        {state.finalVideo && (
          <Button asChild>
            <a href={state.finalVideo} download>
              <Download className="mr-2 h-4 w-4" />
              {t('final.downloadMp4')}
            </a>
          </Button>
        )}
        <Button variant="outline" asChild title={t('final.resolveTitle')}>
          <a href={`/api/assets/${state.id}/final/${title}.fcpxml`} download>
            <Download className="mr-2 h-4 w-4" />
            {t('final.resolve')}
          </a>
        </Button>
        <Button variant="outline" asChild title={t('final.edlTitle')}>
          <a href={`/api/assets/${state.id}/final/${title}.edl`} download>
            <Download className="mr-2 h-4 w-4" />
            {t('final.edl')}
          </a>
        </Button>
        <DownloadProjectButton productionId={state.id} />
        <SendToCuttingRoomButton productionId={state.id} />
        <PromoteStyleButton productionId={state.id} />
      </div>
    </div>
  );
}
